import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom'
import './index.css'
import {
  checkForNewVersion,
  isChunkLoadError,
  isNewVersionAvailable,
  reloadForStaleChunk,
  startFreshBundleWatch,
} from './lib/stale-chunk'

// After a deploy, a browser still on the previous bundle requests a JS chunk whose
// hash no longer exists → recover by reloading once. Failures arrive through three
// channels, so listen on all (the ErrorBoundary handles the React.lazy render-throw).
// The reload is time-boxed in reloadForStaleChunk() so a later deploy still self-heals.
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault() // stop Vite re-throwing; we own the recovery
  reloadForStaleChunk()
})
window.addEventListener('error', (e) => {
  if (isChunkLoadError((e as ErrorEvent).error ?? (e as ErrorEvent).message)) reloadForStaleChunk()
})
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkLoadError((e as PromiseRejectionEvent).reason)) reloadForStaleChunk()
})
import { Footer, Nav } from './components/Nav'
import { AuthProvider } from './lib/auth'
import { AdminPage } from './pages/AdminPage'
import { CarrierPage } from './pages/CarrierPage'
import { CarriersHubPage } from './pages/CarriersHubPage'
import { ComparePage } from './pages/ComparePage'
import { DashboardPage } from './pages/DashboardPage'
// import { DisruptionHubPage } from './pages/DisruptionHubPage'
import { DocsPage } from './pages/DocsPage'
import { HomePage } from './pages/HomePage'
import { LanePage } from './pages/LanePage'
import { LanesHubPage } from './pages/LanesHubPage'
import { LoginPage } from './pages/LoginPage'
import { McpPage } from './pages/McpPage'
import { PortPage } from './pages/PortPage'
import { PortsHubPage } from './pages/PortsHubPage'
import { PricingPage } from './pages/PricingPage'
// import { StormPage } from './pages/StormPage'
// import { StormsIndexPage } from './pages/StormsIndexPage'
import { TradeHubPage } from './pages/TradeHubPage'
import { VesselPage } from './pages/VesselPage'
import { VesselsHubPage } from './pages/VesselsHubPage'
import { VsPage } from './pages/VsPage'
// import { WeatherPage } from './pages/WeatherPage'
import { WelcomePage } from './pages/WelcomePage'

// The Schedules admin carries the NAVO DS stylesheet (~34 KB gz) and its panels; only its visitors
// should download them. A chunk 404 after a deploy is recovered by the listeners above and the
// RouteErrorBoundary below, like any other stale chunk.
const AdminSchedulesPage = React.lazy(() => import('./pages/AdminSchedulesPage'))

function ScrollToTop() {
  const { pathname } = useLocation()
  // Braced on purpose: window.scrollTo() returns undefined today, so the old concise-body arrow was
  // harmless, but it handed that return value to react-dom as the effect's cleanup. If this call is
  // ever swapped for one that returns something else, an undefined-destroy bug becomes a crash.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the scroll trigger
  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

// Stale-chunk PREVENTION: poll /version.json; when a newer deploy is live, reload at the
// NEXT route change — a graceful moment that pre-empts a chunk 404 instead of recovering
// after one. Poll on mount + every 5 min + when the tab regains focus.
function VersionGuard() {
  const { pathname } = useLocation()

  React.useEffect(() => {
    void checkForNewVersion()
    const id = window.setInterval(() => void checkForNewVersion(), 5 * 60_000)
    const onFocus = () => void checkForNewVersion()
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the reload trigger
  React.useEffect(() => {
    if (isNewVersionAvailable()) reloadForStaleChunk()
  }, [pathname])

  return null
}

// Catches a render throw from ANY page so one bad component (or a bad build) degrades to a reload
// prompt instead of a white screen — the failure mode that took the whole app down on 2026-07-18.
// Chunk-load errors self-heal via reloadForStaleChunk(); everything else shows a message + Reload.
// Wraps ONLY <Routes>, so Nav/Footer stay alive and the visitor can navigate away — which clears the
// error (componentDidUpdate on pathname change) without a reload.
class RouteErrorBoundaryInner extends React.Component<
  { pathname: string; children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }
  componentDidCatch(error: unknown) {
    if (isChunkLoadError(error)) reloadForStaleChunk()
  }
  componentDidUpdate(prev: { pathname: string }) {
    if (this.state.hasError && prev.pathname !== this.props.pathname) {
      this.setState({ hasError: false })
    }
  }
  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', padding: '96px 24px', textAlign: 'center' }}>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 700,
            color: 'var(--text-primary)',
            margin: '0 0 12px',
          }}
        >
          Something went wrong on this page
        </h1>
        <p
          style={{
            fontSize: 15,
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
            margin: '0 0 24px',
          }}
        >
          A temporary glitch — reloading usually fixes it. If it keeps happening, let us know.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--on-accent, #fff)',
            background: 'var(--signal, #1f4fe6)',
            border: 'none',
            borderRadius: 10,
            padding: '11px 24px',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    )
  }
}

function RouteErrorBoundary({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation()
  return <RouteErrorBoundaryInner pathname={pathname}>{children}</RouteErrorBoundaryInner>
}

// Last-resort boundary AROUND THE WHOLE APP. RouteErrorBoundary only wraps <Routes>, so a throw in
// Nav, AuthProvider, BrowserRouter, or the Module-Federation React-singleton layer (anything ABOVE
// Routes) had nothing catching it → the entire root white-screened with no message. This catches
// those too, so a component crash NEVER blanks the app again. It sits above the router, so it can't
// use useLocation; the fallback is a full-page branded reload prompt built only from CSS tokens +
// inline styles (no provider/router deps that may themselves have failed). Chunk-load errors after a
// deploy self-heal with one reload; everything else shows the prompt.
class RootErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false }
  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }
  componentDidCatch(error: unknown) {
    if (isChunkLoadError(error)) reloadForStaleChunk()
  }
  render() {
    if (!this.state.hasError) return this.props.children
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          padding: '24px',
          textAlign: 'center',
          background: 'var(--surface-0, #0b0d12)',
          color: 'var(--text-primary, #e8eaed)',
        }}
      >
        <div
          style={{
            fontSize: 20,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            marginBottom: 20,
          }}
        >
          schedules<span style={{ color: 'var(--signal, #1f4fe6)' }}>mcp</span>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 12px' }}>Something went wrong</h1>
        <p
          style={{
            fontSize: 15,
            color: 'var(--text-secondary, #9aa0aa)',
            lineHeight: 1.6,
            margin: '0 0 24px',
            maxWidth: 460,
          }}
        >
          A temporary glitch on our end — reloading usually fixes it. If it keeps happening, let us
          know.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--on-accent, #fff)',
            background: 'var(--signal, #1f4fe6)',
            border: 'none',
            borderRadius: 10,
            padding: '11px 24px',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>
      </div>
    )
  }
}

function App() {
  // Tell the index.html bootstrap watchdog the app actually mounted. If this never fires (the bundle
  // failed to boot, or the tree bailed out — e.g. a stale-chunk mix that trips an invalid-hook call
  // and blanks #root before any ErrorBoundary can render), the watchdog does a one-shot reload.
  React.useEffect(() => {
    document.documentElement.dataset.appReady = '1'
    ;(window as unknown as { __smcpBooted__?: () => void }).__smcpBooted__?.()
  }, [])
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <VersionGuard />
        <Nav />
        <RouteErrorBoundary>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/compare" element={<ComparePage />} />
            <Route path="/vs" element={<VsPage />} />
            <Route path="/vs/:competitor" element={<VsPage />} />
            <Route path="/carriers" element={<CarriersHubPage />} />
            <Route path="/carriers/:slug" element={<CarrierPage />} />
            <Route path="/lanes" element={<LanesHubPage />} />
            <Route path="/lanes/trade/:slug" element={<TradeHubPage />} />
            <Route path="/lanes/:slug" element={<LanePage />} />
            <Route path="/ports" element={<PortsHubPage />} />
            <Route path="/ports/:code" element={<PortPage />} />
            <Route path="/vessels" element={<VesselsHubPage />} />
            <Route path="/vessels/:slug" element={<VesselPage />} />
            {/* Weather is parked with its API surface (see apps/api/src/index.ts). These four pages
                are left in the tree and come back by uncommenting. They are taken down rather than
                left to render because each has an honest empty state that would now be a false
                statement: with the API off they say "no active tropical cyclones" while there are
                active storms. A page that confidently reports nothing is worse than no page. */}
            {/* <Route path="/disruptions" element={<DisruptionHubPage />} /> */}
            {/* <Route path="/storms" element={<StormsIndexPage />} /> */}
            {/* <Route path="/storms/:id" element={<StormPage />} /> */}
            {/* <Route path="/weather" element={<WeatherPage />} /> */}
            <Route path="/mcp" element={<McpPage />} />
            <Route path="/docs" element={<DocsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route
              path="/admin/schedules"
              element={
                <React.Suspense fallback={null}>
                  <AdminSchedulesPage />
                </React.Suspense>
              }
            />
            <Route path="*" element={<HomePage />} />
          </Routes>
        </RouteErrorBoundary>
        <Footer />
      </AuthProvider>
    </BrowserRouter>
  )
}

startFreshBundleWatch()

// biome-ignore lint/style/noNonNullAssertion: #root always present in index.html
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </React.StrictMode>
)
